import { useEffect, useState } from "react"
import { BetSlipFragment } from "../../graphql/core-types"
import { formatCurrency } from "../../helpers/currency"
import { getFundingTotals, getPayoutAmounts } from "../../helpers/bet-line-helpers"
import { WinWagerSummary } from "../bet-credits/bet-credits"
import { useInternalization } from "../../context/cms-context"
import { StakersWon } from "../win-celebration/stakers-won"
import './bet-won-animation.css'

const ENTER_DELAY_MS = 50
const HOLD_DURATION_MS = 3200
const EXIT_DURATION_MS = 500
const STRIPE_TRAVEL_DURATION_S = 5

const STRIPE_COLORS = [
  'var(--pl-g-information-bg)',
  'var(--pl-g-success-bg)',
  'var(--pl-g-warning-bg)',
  'var(--pl-g-purplePayout-bg)',
  'var(--pl-g-bluePayout-bg)',
]
// cycled to build a dense, overlapping field wide enough to cover the whole screen at once
const STRIPE_WIDTH_PATTERN = [120, 260, 160, 320, 140, 220, 280, 130, 300, 190]
const STRIPE_COUNT = 30
const STRIPE_WIDTHS = Array.from({ length: STRIPE_COUNT }, (_, index) => STRIPE_WIDTH_PATTERN[index % STRIPE_WIDTH_PATTERN.length])

export const BetWonAnimation = ({ betSlip, onComplete }: { betSlip: BetSlipFragment, onComplete: () => void }) => {
  const [isActive, setIsActive] = useState(false)
  const { games, translate } = useInternalization()

  useEffect(() => {
    const enterTimeout = setTimeout(() => setIsActive(true), ENTER_DELAY_MS)
    const exitTimeout = setTimeout(() => setIsActive(false), HOLD_DURATION_MS)
    const completeTimeout = setTimeout(() => onComplete(), HOLD_DURATION_MS + EXIT_DURATION_MS)
    return () => {
      clearTimeout(enterTimeout)
      clearTimeout(exitTimeout)
      clearTimeout(completeTimeout)
    }
  }, [])

  const gameName = betSlip.betItems?.[0]?.gameSlug
  const game = games.find(g => g.systemId === gameName)
  const { plBalance, plCredits } = getFundingTotals(betSlip.betItems || [])
  // cash and credits together, like plc-app's settled payout
  const payoutAmounts = getPayoutAmounts(betSlip)

  return (
    <div className={`bet_won__overlay ${isActive ? 'bet_won__overlay--active' : 'bet_won__overlay--inactive'}`}>
      <div className="bet_won__stripes">
        {STRIPE_WIDTHS.map((width, index) => (
          <div
            key={index}
            className="bet_won__stripe"
            style={{
              width: `${width}px`,
              marginLeft: `-${width / 2}px`,
              backgroundColor: STRIPE_COLORS[index % STRIPE_COLORS.length],
              animationDuration: `${STRIPE_TRAVEL_DURATION_S}s`,
              animationDelay: `-${(index * STRIPE_TRAVEL_DURATION_S) / STRIPE_COUNT}s`,
              animationDirection: index % 2 === 0 ? 'normal' : 'reverse',
            }}
          />
        ))}
      </div>
      <div className="bet_won__scrim" />
      <div className="bet_won__hero">
        <span className="bet_won__label pl-fontL">You Won!</span>
        <span className="bet_won__amount pl-font4XL">{formatCurrency(String(payoutAmounts.dollars + payoutAmounts.credits))}</span>
        <WinWagerSummary
          className="bet_won__subtext pl-fontM"
          gameLabel={translate(`${game?.displayName || gameName}`) || null}
          plBalance={plBalance}
          plCredits={plCredits}
          creditsPaidOut={payoutAmounts.credits}
        />
        <StakersWon betSlip={betSlip} className="bet_won__stakers pl-fontL" />
      </div>
    </div>
  )
}
